import { IndexedDbReadingStore } from "./indexedDbStore";
import { ServerReadingStore } from "./serverStore";
import type { ReadingStore } from "./types";

export * from "./types";

const local = new IndexedDbReadingStore();
const remote = new ServerReadingStore();

/**
 * Which store this browser is talking to, decided once and remembered.
 *
 * Signed in, a reading belongs to the account and lives in Postgres, so it
 * is there on the student's other devices. Signed out, there is nowhere to
 * put it but the browser, and the anonymous Quick read still has to work.
 *
 * The probe failing — offline, or a test environment with no server — means
 * IndexedDB. That is the conservative answer: the worst case is a reading
 * saved locally that has to be migrated later, rather than a save that
 * throws and loses the student's document.
 */
let choice: Promise<ReadingStore> | null = null;

function resolveStore(): Promise<ReadingStore> {
  if (!choice) {
    choice = fetch("/api/auth/get-session", { credentials: "same-origin" })
      .then((response) => (response.ok ? response.json() : null))
      // Any signed-in user writes to the account. This used to require a
      // verified address, which meant an unverified account silently wrote
      // to IndexedDB and its library looked empty on every other device.
      .then((session: { user?: unknown } | null) =>
        session?.user ? remote : local,
      )
      .catch(() => local);
  }
  return choice;
}

/**
 * Call after signing in or out. Without it the module keeps the store it
 * picked when the page first loaded, and a student who signs in mid-session
 * carries on writing into IndexedDB.
 */
export function refreshStorageMode(): void {
  choice = null;
}

/** True when this browser is writing to the account rather than to itself. */
export async function usingAccountStorage(): Promise<boolean> {
  return (await resolveStore()) === remote;
}

/** The local store, for the migration to read out of. */
export const localReadingStore: ReadingStore = local;

/**
 * A façade rather than one of the two stores directly: which one is correct
 * is not known until the session has been read, and every call site expects
 * a plain object it can call straight away.
 */
export const readingStore: ReadingStore = {
  addReading: async (reading) => (await resolveStore()).addReading(reading),
  listReadings: async () => (await resolveStore()).listReadings(),
  getReading: async (id) => (await resolveStore()).getReading(id),
  saveRebuild: async (rebuild) => (await resolveStore()).saveRebuild(rebuild),
  getRebuild: async (readingId, mode) =>
    (await resolveStore()).getRebuild(readingId, mode),
  saveDeepReadSession: async (session) =>
    (await resolveStore()).saveDeepReadSession(session),
  getDeepReadSession: async (id) =>
    (await resolveStore()).getDeepReadSession(id),
  listDeepReadSessionsForReading: async (readingId) =>
    (await resolveStore()).listDeepReadSessionsForReading(readingId),
  renameReading: async (id, title) =>
    (await resolveStore()).renameReading(id, title),
  deleteReading: async (id) => (await resolveStore()).deleteReading(id),
};
